<template>
  <div class="vfriends">
    <div class="vfriends__search">
      <i class="el-icon-search vfriends__search-icon"></i>
      <input
        v-model="keyword"
        class="vfriends__input"
        type="search"
        placeholder="搜索关注的人"
        maxlength="30"
        @input="onSearchInput"
        @keydown.enter.prevent="reload"
      />
      <i v-if="keyword" class="el-icon-circle-close vfriends__clear" @click="clearSearch"></i>
    </div>

    <div ref="list" class="vfriends__list" @scroll="onScroll">
      <div v-if="loading && !list.length" class="vfriends__state">加载中...</div>
      <div v-else-if="loadError && !list.length" class="vfriends__state vfriends__state--retry" @click="reload">
        加载失败，点击重试
      </div>
      <div v-else-if="!list.length" class="vfriends__state">{{ searching ? '未找到相关好友' : '还没有关注的人' }}</div>

      <div v-for="friend in list" :key="friend.key" class="vfriends__item">
        <smart-img mode="avatar" :src="friend.avatar" :alt="friend.nickname" class="vfriends__avatar" />
        <span class="vfriends__name">{{ friend.nickname }}</span>
        <button
          type="button"
          class="vfriends__btn"
          :class="{
            'is-sending': sendingKey === friend.key,
            'is-done': sendingKey !== friend.key && sharedKeys.indexOf(friend.key) !== -1
          }"
          :disabled="sendingKey === friend.key"
          @click="$emit('share', friend)"
        >
          {{ sendingKey === friend.key ? '发送中' : (sharedKeys.indexOf(friend.key) !== -1 ? '已分享' : '分享') }}
        </button>
      </div>

      <div v-if="list.length" class="vfriends__footer">
        {{ loading ? '加载中...' : (hasMore ? '' : '没有更多了') }}
      </div>
    </div>
  </div>
</template>

<script>
import { userFetch } from '@/api'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

// 接口回来的是 UserInfoVO（id / nickname / avatar），整理成和分享面板那一排同样的形状
function normalizeFriend (raw) {
  const id = raw && (raw.id || raw.userId)
  if (!id) return null
  return {
    key: String(id),
    isGroup: false,
    userId: String(id),
    groupId: '',
    nickname: raw.nickname || '用户',
    avatar: raw.avatar || ''
  }
}

/**
 * 私信好友：带搜索、能翻页的完整好友列表（我关注的人），对齐 App 端 DmFriendsSheet。
 * 分享面板那一排只有最近会话，找不到人时从这里搜。
 * 这里只管选人，发送交给播放器（share 事件）：和那一排共用发送状态，分享过的人这里直接显示「已分享」。
 */
export default {
  name: 'VideoFriendPicker',
  props: {
    sendingKey: { type: String, default: '' },
    sharedKeys: { type: Array, default: () => [] }
  },
  data () {
    return {
      keyword: '',
      list: [],
      pageNo: 1,
      hasMore: true,
      loading: false,
      loadError: false
    }
  },
  computed: {
    searching () {
      return !!this.keyword.trim()
    }
  },
  created () {
    this.seq = 0
    this.searchTimer = null
    this.reload()
  },
  beforeDestroy () {
    this.destroyed = true
    clearTimeout(this.searchTimer)
  },
  methods: {
    reload () {
      clearTimeout(this.searchTimer)
      // 序号加一：还在路上的旧请求（上一个关键词）回来直接丢掉
      this.seq += 1
      this.list = []
      this.pageNo = 1
      this.hasMore = true
      this.loading = false
      this.loadError = false
      this.loadPage(1)
    },
    async loadPage (pageNo) {
      const seq = this.seq
      const keyword = this.keyword.trim()
      this.loading = true
      this.loadError = false
      try {
        const params = { pageNo, pageSize: PAGE_SIZE }
        const res = keyword
          ? await userFetch.searchMyFollowed({ ...params, keyword })
          : await userFetch.getMyFriends(params)
        if (this.destroyed || seq !== this.seq) return
        const data = (res && res.data) || {}
        const records = Array.isArray(data.records) ? data.records : []
        const seen = new Set(pageNo === 1 ? [] : this.list.map((item) => item.key))
        const fresh = []
        records.forEach((raw) => {
          const friend = normalizeFriend(raw)
          if (!friend || seen.has(friend.key)) return
          seen.add(friend.key)
          fresh.push(friend)
        })
        this.list = pageNo === 1 ? fresh : this.list.concat(fresh)
        this.pageNo = pageNo
        // 这一页没带来新的人也当到底了，免得 total 对不上时一直往下翻
        this.hasMore = fresh.length > 0 && this.list.length < (Number(data.total) || 0)
      } catch (e) {
        if (this.destroyed || seq !== this.seq) return
        if (pageNo === 1) this.loadError = true
      } finally {
        if (seq === this.seq) this.loading = false
      }
    },
    onScroll () {
      const el = this.$refs.list
      if (!el || this.loading || !this.hasMore || this.loadError) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.loadPage(this.pageNo + 1)
      }
    },
    onSearchInput () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        if (!this.destroyed) this.reload()
      }, SEARCH_DEBOUNCE_MS)
    },
    clearSearch () {
      this.keyword = ''
      this.reload()
    }
  }
}
</script>

<style lang="scss" scoped>
.vfriends {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.vfriends__search {
  position: relative;
  flex-shrink: 0;
  margin: 12px 16px 8px;
  height: 36px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 18px;
  background: #f4f5f7;
}

.vfriends__search-icon {
  color: #999;
  font-size: 15px;
}

.vfriends__input {
  flex: 1;
  min-width: 0;
  margin-left: 6px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #1a1a1a;

  &::-webkit-search-cancel-button {
    display: none;
  }
}

.vfriends__clear {
  color: #c0c4cc;
  font-size: 16px;
  cursor: pointer;
}

.vfriends__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 16px 12px;
}

.vfriends__state {
  padding: 48px 0;
  text-align: center;
  font-size: 13px;
  color: #b0b0b0;

  &--retry {
    cursor: pointer;
    color: #409eff;
  }
}

.vfriends__item {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.vfriends__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.vfriends__name {
  flex: 1;
  min-width: 0;
  margin: 0 12px;
  font-size: 14px;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vfriends__btn {
  flex-shrink: 0;
  min-width: 64px;
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 15px;
  background: #ff2442;
  color: #fff;
  font-size: 13px;
  cursor: pointer;

  &.is-sending {
    background: #ffb3bd;
    cursor: default;
  }

  &.is-done {
    background: #f4f5f7;
    color: #999;
  }
}

.vfriends__footer {
  padding: 8px 0;
  text-align: center;
  font-size: 12px;
  color: #c0c4cc;
  min-height: 16px;
}
</style>
